'use client';

import { useState } from 'react';
import { getYouTubeEmbedUrl, getYouTubeThumbnailUrl, isValidYouTubeUrl, getYouTubeVideoId } from '@/slw/shared/lib/youtube-validation';

interface ListingVideosProps {
  videoUrls: string[];
}

export function ListingVideos({ videoUrls }: ListingVideosProps) {
  const [playingVideo, setPlayingVideo] = useState<number | null>(null);
  
  // Filter out empty or invalid URLs
  const validVideos = videoUrls.filter(url => url && url.trim() !== '' && isValidYouTubeUrl(url));

  if (validVideos.length === 0) {
    return null;
  }

  const handlePlayVideo = (index: number) => {
    setPlayingVideo(index);
  };

  const handleOpenVideoPopup = (videoUrl: string) => {
    const videoId = getYouTubeVideoId(videoUrl);
    if (!videoId) return;

    const embedUrl = getYouTubeEmbedUrl(videoUrl);
    if (!embedUrl) return;

    // Calculate popup dimensions (80% of screen, max 1200x800)
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const popupWidth = Math.min(screenWidth * 0.8, 1200);
    const popupHeight = Math.min(screenHeight * 0.8, 800);
    
    // Center the popup
    const left = (screenWidth - popupWidth) / 2;
    const top = (screenHeight - popupHeight) / 2;

    // Open popup window
    const popup = window.open(
      embedUrl,
      'youtube-video',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no`
    );

    if (popup) {
      popup.focus();
    }
  };

  const handleCloseVideo = () => {
    setPlayingVideo(null);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">Videos</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {validVideos.map((videoUrl, index) => {
          const embedUrl = getYouTubeEmbedUrl(videoUrl);
          const thumbnailUrl = getYouTubeThumbnailUrl(videoUrl);
          const videoId = getYouTubeVideoId(videoUrl);
          
          if (!embedUrl || !thumbnailUrl || !videoId) {
            return null;
          }

          return (
            <div key={index} className="relative group">
              {playingVideo === index ? (
                // Video player with popup option
                <div className="relative">
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <iframe
                      src={embedUrl}
                      title={`Video ${index + 1}`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={() => handleOpenVideoPopup(videoUrl)}
                      className="bg-blue-600 bg-opacity-80 text-white rounded-full p-2 hover:bg-opacity-100 transition-opacity"
                      title="Open in popup window"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>
                    <button
                      onClick={handleCloseVideo}
                      className="bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-75 transition-opacity"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ) : (
                // Video thumbnail preview
                <div 
                  className="relative cursor-pointer overflow-hidden rounded-lg"
                  onClick={() => handlePlayVideo(index)}
                >
                  <div className="aspect-video bg-gray-100">
                    <img
                      src={thumbnailUrl}
                      alt={`Video ${index + 1} thumbnail`}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                  </div>
                  {/* Popup button overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-opacity">
                    <div 
                      className="bg-red-600 rounded-full p-4 group-hover:scale-110 transition-transform cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenVideoPopup(videoUrl);
                      }}
                      title="Open in popup window"
                    >
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </div>
                  </div>
                  {/* Video duration or title overlay */}
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                    Video {index + 1}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}